<script>
    import yup, {object, number} from 'yup';
    
    import {PATH_PREFIX} from '../../../apiUrls.js';
    import {sgettext} from '../../../i18n.js';
    import {get, post} from '../../../utils.js';

    import ApproversList from '../../Diff/ApproversList.svelte';
    import ComboSelect from '../../ComboSelect/ComboSelect.svelte';
    import Errors from '../../Errors.svelte';

    export let activityId = null;
    export let id = 'workflowManagement';

    let workCentreId;
    let reviewersUrl = `${PATH_PREFIX}/sw/api/builder/activity/${activityId}/reviewers/`;
    let strategyApproversUrl = `${PATH_PREFIX}/sw/api/builder/activity/${activityId}/strategy-approvers/`
    let activityApproversUrl = `${PATH_PREFIX}/sw/api/builder/activity/${activityId}/activity-approvers/`;

    $: workCentreActivityApproversUrl = `${PATH_PREFIX}/sw/api/builder/activity/${workCentreId}/activity-approvers/`;

    // Starts listening for changes in the root HTML element of the page. "app/builder/templates/builder/activity/activity_update.html"
    // In this case, we're watching for changes to the disabled attribute of the element with id 'id_workflow_management'
    // This is a way to get around the fact that the edit disabled attribute is not reactive

    let workflow_parent_div = document.getElementById('id_workflow_management');
    let work_centre_element = document.getElementById("id_work_centre");

    let mutationObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if(mutation.target === workflow_parent_div) {
                disabled = workflow_parent_div.hasAttribute('disabled');
            }
            if (mutation.target === work_centre_element) {
                workCentreId = work_centre_element.dataset.workCentre;
            }
        });
    });

    mutationObserver.observe(document.getElementById("id_work_centre"), {
        attributes: true,
    });

    mutationObserver.observe(document.getElementById('id_workflow_management'), {
        attributes: true,
    });

    let errors;
    let error_sup_role = "";
    let fieldValidations = getDefaultFieldValidations();

    $: disabled = false;
    $: reviewers = null;
    $: reviewerOptions = null;
    $: strategyApproverOptions = null;
    $: strategyApprovers = null;
    $: activityApprovers = null;

    $: if(strategyApprovers?.length == 0) {
        errors = {"error": sgettext("$$LEAST_ONE_STRATEGY_APPROVER_REQUIRED$$"), "sup_role": "Strategy Approver"};
    } else {
        errors = '';
    }

    $: if(errors) {
        error_sup_role = errors.sup_role ?? "";
        errors = errors.error ?? errors;
    }

    let selectedStrategyApprover = getDefault();
    let selectedReviewer = getDefault();

    function getDefault() {
        return {
            siteUserPositionId: null,
            expire: false,
            fieldValidations: getDefaultFieldIsValid(),
        };
    }

    function getDefaultFieldIsValid() {
        return {
            siteUserPositionId: {
                isValid: true,
                errors: '',
            },
        };
    }

    function getDefaultFieldValidations() {
        return {
            strategy_approver: {
                isValid: true,
                errors: '',
            },
            reviewer: {
                isValid: true,
                errors: '',
            },
        };
    }

    let schema = object().shape({
        siteUserPositionId: number()
            .required(sgettext('$$REQUIRED_FIELD$$'))
            .typeError(sgettext('$$REQUIRED_FIELD$$')),
        strategy_approver: number()
            .transform((value) => isNaN(value) ? null : value)
            .nullable(),
        reviewer: number()
            .transform((value) => isNaN(value) ? null : value)
            .nullable(),
    });

    async function getActivityApproversForWorkCentre(url) {
        if(workCentreId && workCentreId != 0) {
            const response = await get(url);
            if (response.ok) {
                const res = await response.json();
                activityApprovers = res.activity_approvers;
            } else {
                const error = await response.json();
                if (error.hasOwnProperty('error')) {
                    errors = error;
                }
            }
        } else {
            activityApprovers = [];
        }
    }

    async function getActivityApprovers() {
        const response = await get(activityApproversUrl);
        if (response.ok) {
            const res = await response.json();
            activityApprovers = res.activity_approvers;
        } else {
            const error = await response.json();
            if (error.hasOwnProperty('error')) {
                errors = error;
            }
        }
    }

    async function getStrategyApprovers() {
        const response = await get(strategyApproversUrl);
        if (response.ok) {
            const res = await response.json();
            strategyApprovers = res.strategy_approvers;
        } else {
            const error = await response.json();
            if (error.hasOwnProperty('error')) {
                errors = error;
            }
        }
        getStrategyApproverOptions();
    }

    async function getStrategyApproverOptions() {
        const response = await get(strategyApproversUrl + 'options/');
        if (response.ok) {
            const res = await response.json();
            strategyApproverOptions = res.strategy_approver_options.map((r) => ({...r, fieldValidations: getDefaultFieldIsValid()}));
        } else {
            const error = await response.json();
            if (error.hasOwnProperty('error')) {
                errors = error;
            }
        }
    }

    async function updateStrategyApprover(id, remove) {
        try {
            const response = await post(strategyApproversUrl + 'update/', {strategy_approver_id: id, remove: remove});
            if (response.ok) {
                const res = await response.json();
                if (res) {
                    getStrategyApprovers();
                }
            } else {
                const error = await response.json();
                if (error.hasOwnProperty('error')) {
                    errors = error;
                }
            }
        } catch (err) {
            errors = {"error": sgettext("$$FAILED_UPDATE_STRATEGY_APPROVER$$"), "sup_role": "Strategy Approver"};
        } finally {
            // clearing off the user selection
            selectedStrategyApprover = getDefault();
        }
    }
    async function createStrategyApprover(){
        if(isNaN(selectedStrategyApprover)) return;

        updateStrategyApprover(selectedStrategyApprover, false)
    }

    async function deleteStrategyApprover(row){
        updateStrategyApprover(row.detail.id, true)
    }

    async function getReviewers() {
        const response = await get(reviewersUrl);
        if (response.ok) {
            const res = await response.json();
            reviewers = res.reviewers;
        } else {
            const error = await response.json();
            if (error.hasOwnProperty('error')) {
                errors = error;
            }
        }
        getReviewerOptions();
    }

    async function getReviewerOptions() {
        const response = await get(reviewersUrl + 'options/');
        if (response.ok) {
            const res = await response.json();
            reviewerOptions = res.reviewer_options.map((r) => ({...r, fieldValidations: getDefaultFieldIsValid()}));
        } else {
            const error = await response.json();
            if (error.hasOwnProperty('error')) {
                errors = error;
            }
        }
    }

    async function updateReviewer(id, change_type, remove) {
        try {
            let data = {
                reviewer_id: id,
                change_type: change_type,
                remove: remove
            }
            if (change_type||remove){data.group_type = reviewers.find((approver) => approver.id === id).group_type}
            const response = await post(reviewersUrl + 'update/', data);
            if (response.ok) {
                const res = await response.json();
                if (res) {
                    getReviewers();
                }
            } else {
                const error = await response.json();
                if (error.hasOwnProperty('error')) {
                    errors = error;
                }
            }
        } catch (err) {
            errors = {"error": sgettext("$$FAILED_UPDATE_REVIEWER$$"), "sup_role": "Reviewer"};
        } finally {
            // clearing off the user selection
            selectedReviewer = getDefault();
        }
    }
    async function createReviewer(){
        if(isNaN(selectedReviewer)) return;

        updateReviewer(selectedReviewer, false, false)
    }

    async function deleteReviewer(row){
        updateReviewer(row.detail.id, false, true)
    }

    async function updateReviewerType(row){
        updateReviewer(row.detail.id, true, false)
    }

    $: getActivityApproversForWorkCentre(workCentreActivityApproversUrl);
    $: getActivityApprovers();
    $: getStrategyApprovers();
    $: getReviewers();

    $: {
        if (disabled) {
            errors = null;
            fieldValidations = getDefaultFieldValidations();
        }
    }

</script>

<label>
  {sgettext('$$ACTIVITY_APPROVERS$$')}
  <a
    href="#"
    data-toggle="tooltip"
    title={sgettext(
      '$$ACTIVITY_APPROVER_NEEDS_SET_RESPONSIBLE$$'
    )}
    style="color:#ccc; margin-left:5px;"
  >
    <i class="fas fa-question-circle" />
  </a>
</label>
<ApproversList
  title={''}
  noApproversLabel={sgettext('$$ACTIVITY_APPROVERS_CONFIGURED_1$$')}
  users={activityApprovers}
  userType="activity-approver"
  readonly={true}
  isExpanded={true}
  fixedTable={true}
  scrollHeight="176px"
/>

{#if errors && error_sup_role !== 'Reviewer'}
  <div class="mt-3">
    <Errors {errors} />
  </div>
{/if}

<label class="mt-2">
  {sgettext('$$STRATEGY_APPROVERS$$')}
  <a
    href="#"
    data-toggle="tooltip"
    title={sgettext(
      '$$STRATEGY_APPROVER_NEEDS_SET_RESPONSIBLE$$'
    )}
    style="color:#ccc; margin-left:5px;"
  >
    <i class="fas fa-question-circle" />
  </a>
</label>
{#if !disabled}
  <div style="padding: .25rem !important ">
    <ComboSelect
      id="strategy_approver"
      label={''}
      searchPlaceholderText={sgettext(
        '$$CLICK_SELECT_ADD_STRATEGY_APPROVERS_$$'
      )}
      bind:options={strategyApproverOptions}
      bind:value={selectedStrategyApprover}
      bind:isValid={fieldValidations.strategy_approver.isValid}
      bind:errors={fieldValidations.strategy_approver.errors}
      on:changed={createStrategyApprover}
      searchable={true}
      editable={!disabled}
      {schema}
    />
  </div>
{/if}

<ApproversList
  title={''}
  noApproversLabel={sgettext('$$STRATEGY_APPROVERS_CONFIGURED$$')}
  readonly={disabled}
  users={strategyApprovers}
  userType="strategy-approver"
  isExpanded={true}
  fixedTable={true}
  scrollHeight="176px"
  on:bypass={deleteStrategyApprover}
/>

{#if errors && error_sup_role === 'Reviewer'}
  <div class="mt-3">
    <Errors {errors} />
  </div>
{/if}

<label class="mt-2">
  {sgettext('$$REVIEWERS$$')}
  <a
    href="#"
    data-toggle="tooltip"
    title={sgettext(
      '$$REVIEWER_OPTIONAL_ROLE_SET_INCLUDE$$'
    )}
    style="color:#ccc; margin-left:5px;"
  >
    <i class="fas fa-question-circle" />
  </a>
</label>
{#if !disabled}
  <div style="padding: .25rem !important">
    <ComboSelect
      id="reviewer"
      label={''}
      searchPlaceholderText={sgettext('$$CLICK_SELECT_ADD_REVIEWERS_$$')}
      bind:options={reviewerOptions}
      bind:value={selectedReviewer}
      bind:isValid={fieldValidations.reviewer.isValid}
      bind:errors={fieldValidations.reviewer.errors}
      on:changed={createReviewer}
      searchable={true}
      {schema}
      editable={!disabled}
    />
  </div>
{/if}

<ApproversList
  title={''}
  noApproversLabel={sgettext('$$REVIEWERS_CONFIGURED$$')}
  readonly={disabled}
  users={reviewers}
  userType="reviewer"
  isExpanded={true}
  fixedTable={true}
  scrollHeight="183px"
  on:bypass={deleteReviewer}
  on:regroup={updateReviewerType}
/>
